/* eslint-disable @typescript-eslint/restrict-template-expressions */
import {
  Typography,
  useTheme,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  type GridRowData,
  type GridColumns,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { CustomDataGrid, DialogBox } from 'components';
import { colorTheme } from 'theme';
import { useEffect, useState } from 'react';
import Buttons from 'components/elements/Buttons';
import {
  confirmationIBorder,
  createAndPlaceOrder,
  getAllOrderList,
} from 'service';
import OrderStatus from './OrderStatus';
import { strings } from 'i18n/Strings';
import { ErrorModel, YesNoModal } from 'components/shared';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import { useDispatch, useSelector } from 'react-redux';
import { checkSortingValues, dateFormat } from 'utils/GeneralUtil';
import {
  displayUserUnitsPerOrder,
  exportAllOrderHistoryList,
} from 'service/OrderService';
import { CurrencyFormater } from 'components/helpers/commonFunctions/CurrencyFormater';
import {
  exportExcelFileNames,
  statusOptions,
  transactionTypeOptions,
  commonConstantTexts,
} from 'constants/constantText';
import { LoadingButton } from '@mui/lab';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';

export interface OrderHistoryInterface {
  mmOrderId?: number;
  ibOrderId?: string;
  fundId?: number;
  mmTransactionId?: number;
  orderStatus?: string;
  totalUnits?: number;
  orderDate?: string;
  transactionMethod?: string;
  isOrderCancelled?: boolean;
  totalContribution?: string;
  contractId?: number;
}

export interface IUserUnits {
  userId: number;
  unitsAllocated: string;
  allocationAmount: string;
  orderInfo: { tradePrice: string };
  userInfo: {
    email: string;
    firstName: string;
    lastName: string;
  };
}

export interface OrderHistoryFilterType {
  startDate?: string | undefined;
  endDate?: string;
  status?: string;
  orderType?: string;
  employerId?: string;
}

const OrderHistory = (): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const [planData, setPlanData] = useState<OrderHistoryInterface[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rowIBOrderId, setRowIBOrderId] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isOrderClicked, setIsOrderClicked] = useState(false);
  const [orderConfirmation, setOrderConfirmation] = useState([]);
  const [orderData, setOrderData] = useState<OrderHistoryInterface>();
  const [userAllocatedUnits, setUserUnits] = useState<IUserUnits[] | null>(
    null,
  );
  const [openUserModal, setOpenUserModal] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [pageCount, setPageCount] = useState(null);
  const [filterLoader, setFilterLoader] = useState<boolean>(false);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [filters, setFilters] = useState<OrderHistoryFilterType>({
    startDate: undefined,
    endDate: undefined,
    status: 'All',
    orderType: 'All',
  });
  const [ibError, setIbError] = useState<{
    information: string;
    open: boolean;
    title: string;
  }>({ information: '', open: false, title: '' });
  const [dateError, setDateError] = useState<string>('');

  const pageInfo = useSelector((state) => state?.pagination?.orderHistory);
  const dispatch = useDispatch();
  const getAllPlanRequests = async (): Promise<void> => {
    try {
      const data = await getAllOrderList(
        Number(pageInfo?.page) + 1,
        pageInfo?.pageSize,
        pageInfo?.sortBy,
        pageInfo?.sortOrder,
        filters,
      );
      setPlanData(data?.rows);
      setPageCount(data?.count);
    } catch (error) {
      console.log(error);
    }
    setFilterLoader(false);
  };

  const handleFilterChange = (filterType: string, value: string): void => {
    const updatedFilters = {
      ...filters,
      [filterType]: value,
    };

    // Validate dates
    if (filterType === 'startDate' || filterType === 'endDate') {
      if (updatedFilters.startDate && updatedFilters.endDate) {
        const startDate = new Date(updatedFilters.startDate);
        const endDate = new Date(updatedFilters.endDate);
        
        if (endDate < startDate) {
          setDateError('End date cannot be before start date');
        } else {
          setDateError('');
        }
      } else {
        setDateError('');
      }
    }

    setFilters(updatedFilters);
  };

  const onClickePlaceOrder = (orderData: OrderHistoryInterface): void => {
    const orderDataWithMethod = {
      ...orderData,
      orderMethod: orderData.transactionMethod,
    };
    setOrderData(orderDataWithMethod);
    createAndPlaceOrder(orderDataWithMethod)
      .then(async (res: never) => {
        if (res.error) {
          setIbError({
            information: res.error || '',
            open: true,
            title: strings.displayText.idErrorHeader,
          });

          setOpenConfirmationDialog(false);
        } else if (res?.[0]?.order_id) {
          setOrderConfirmation(res);
          setOpenConfirmationDialog(true);
          dispatch(
            setSnackBarSuccess({
              snackBarMessage: strings.displayText.orderPlacedText,
            }),
          );
          setIsOrderClicked(true);
          await getAllPlanRequests();
        }
      })
      .catch((err) => {
        setIbError({
          information: err?.response?.data?.message || '',
          open: true,
          title: strings.displayText.idErrorHeader,
        });
      });
  };
  const confirmIBOrder = async (): Promise<void> => {
    const confirmationVal = {
      orderId: orderConfirmation[0].id,
      confirmation: true,
      mmOrderId: orderData?.mmOrderId,
      ...orderData,
    };
    const data = await confirmationIBorder(confirmationVal);
    if (!data?.isComplete) {
      setOrderConfirmation(data);
      setOpenConfirmationDialog(true);
    } else {
      dispatch(
        setSnackBarSuccess({
          snackBarMessage: strings.displayText.orderPlacedText,
        }),
      );
      setOpenConfirmationDialog(false);
      await getAllPlanRequests();
    }
  };

  const handleChatDialogClose = (): void => {
    setIsModalOpen(false);
  };

  const UserUnitsModal = ({ orderId }: { orderId: string }): JSX.Element => {
    const userUnitsPerOrder = async (orderId: number): Promise<void> => {
      try {
        const res = await displayUserUnitsPerOrder(Number(orderId));
        setUserUnits(res?.userUnits);
      } catch (error) {
        console.log(error);
      }
    };

    useEffect(() => {
      if (userAllocatedUnits === null && orderId !== null) {
        void userUnitsPerOrder(Number(orderId));
      }
    }, [orderId, userAllocatedUnits]);

    if (userAllocatedUnits === null) return <></>;

    const columns: GridColumns<GridValidRowModel> = [
      {
        field: 'userId',
        flex: 1,
        width: 100,
        cellClassName: 'name-column--cell',
        renderHeader: () => (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {strings.displayText.userId}
          </Typography>
        ),
        renderCell: ({ row }) => (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row.userId}
          </Typography>
        ),
      },
      {
        field: 'unitsAllocated',
        flex: 1,
        width: 100,
        cellClassName: 'name-column--cell',
        renderHeader: () => (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {strings.displayText.unitsAllocated}
          </Typography>
        ),
        renderCell: ({ row }) => (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {Number(row.unitsAllocated).toFixed(7)}
          </Typography>
        ),
      },
      {
        field: 'allocationAmount',
        flex: 1.7,
        width: 160,
        cellClassName: 'name-column--cell',
        headerAlign: 'right',
        align: 'right',
        renderHeader: () => (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {strings.displayText.contributedAmount}
          </Typography>
        ),
        renderCell: ({ row }) => (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {Number(row.allocationAmount).toFixed(7)}
          </Typography>
        ),
      },
      {
        field: 'tradePrice',
        flex: 1.3,
        width: 120,
        cellClassName: 'name-column--cell',
        headerAlign: 'right',
        align: 'right',
        renderHeader: () => (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {strings.displayText.pricePerUnit}
          </Typography>
        ),
        renderCell: ({ row }) => (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {CurrencyFormater(Number(row.orderInfo?.tradePrice))}
          </Typography>
        ),
      },
      {
        field: 'name',
        flex: 1.5,
        width: 140,
        cellClassName: 'name-column--cell',
        renderHeader: () => (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {strings.displayText.userName}
          </Typography>
        ),
        renderCell: ({ row }) => (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {`${row.userInfo?.firstName ?? ''} ${row.userInfo?.lastName ?? ''}`}
          </Typography>
        ),
      },
      {
        field: 'email',
        flex: 2.3,
        width: 200,
        cellClassName: 'name-column--cell',
        renderHeader: () => (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {strings.displayText.email}
          </Typography>
        ),
        renderCell: ({ row }) => (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row.userInfo?.email}
          </Typography>
        ),
      },
    ];

    return (
      <Box mt={-4} padding={1} sx={{ minWidth: 1050 }}>
        {userAllocatedUnits?.length > 0 ? (
          <CustomDataGrid
            columns={columns}
            rows={userAllocatedUnits}
            getRowId={(row) => row.userId}
            autoHeight
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0
                ? commonConstantTexts.evenText
                : commonConstantTexts.oddText
            }
          />
        ) : (
          <Box padding={5}>
            <Typography color={colors.gray[100]}>
              {strings.displayText.noUnitsAllocated}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  useEffect(() => {
    void getAllPlanRequests();
  }, [
    pageInfo?.page,
    pageInfo?.pageSize,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
  ]);

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'mmOrderId',
      flex: 1,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.mmOrderId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.mmOrderId}
        </Typography>
      ),
    },
    {
      field: 'transactionMethod',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.orderType}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.transactionMethod}
        </Typography>
      ),
    },
    {
      field: 'orderDate',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.orderDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row.orderDate)}
        </Typography>
      ),
    },
    {
      field: 'tradeDate',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.tradeDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row.tradeDate)}
        </Typography>
      ),
    },
    {
      field: 'filledDate',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.filledDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.filledDate ? dateFormat(row.filledDate) : '-'}
        </Typography>
      ),
    },
    {
      field: 'settlementDate',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.settlementDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.settlementDate ? dateFormat(row.settlementDate) : '-'}
        </Typography>
      ),
    },
    {
      field: 'ibOrderId',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.ibOrderId}
        </Typography>
      ),
      flex: 1,
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          <a
            style={{ color: 'blue', textDecoration: 'underline' }}
            onClick={() => {
              setRowIBOrderId(row.mmOrderId);
              setIsModalOpen(true);
            }}
          >
            {row.ibOrderId}
          </a>
        </Typography>
      ),
    },
    {
      field: 'orderStatus',
      flex: 1,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.orderResponse}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.orderStatus === strings.displayText.cancelled
            ? strings.displayText.rejected
            : row.orderStatus}
        </Typography>
      ),
    },
    {
      field: 'unitAllocationStatus',
      flex: 1.2,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.allocationStatus}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <>
          {row.unitAllocationStatus ? (
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              <a
                style={{ color: 'blue', textDecoration: 'underline' }}
                onClick={() => {
                  setOrderId(row.mmOrderId);
                  setOpenUserModal(true);
                }}
              >
                {strings.displayText.allocated}
              </a>
            </Typography>
          ) : (
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {strings.displayText.pendingAllocation}
            </Typography>
          )}
        </>
      ),
    },
    {
      field: 'retriggerOrder',
      flex: 1.25,
      sortable: false,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.retriggerOrder}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.isOrderCancelled ? (
            <Buttons
              onClick={() => {
                onClickePlaceOrder(row);
              }}
            >
              {strings.displayText.retriggerOrder}
            </Buttons>
          ) : null}
        </Typography>
      ),
    },
  ];

  const downloadExcelFile = async (): Promise<void> => {
    setExportLoader(true);

    if (dateError) {
      setExportLoader(false);
      dispatch(
        setSnackBarFailed({
          snackBarMessage: dateError,
        }),
      );
      return;
    }

    if (filters.startDate && filters.endDate) {
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      
      if (endDate < startDate) {
        setExportLoader(false);
        dispatch(
          setSnackBarFailed({
            snackBarMessage: 'End date cannot be before start date',
          }),
        );
        return;
      }
      
      const dateDifference =
        (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24);

      if (dateDifference > 60) {
        setExportLoader(false);
        dispatch(
          setSnackBarFailed({
            snackBarMessage: strings.displayText.dateExceeded,
          }),
        );
        return;
      }
    }
    if (!filters.startDate || !filters.endDate) {
      setExportLoader(false);
      dispatch(
        setSnackBarFailed({
          snackBarMessage: strings.displayText.selectDateRange,
        }),
      );
      return;
    }

    try {
      const url = await exportAllOrderHistoryList(filters);
      const link = document.createElement('a');
      link.href = url as string;
      link.download = exportExcelFileNames.ORDERS_HISTORY;
      link.click();
      dispatch(
        setSnackBarSuccess({
          snackBarMessage: strings.displayText.orderHistoryExportSuccess,
        }),
      );
    } catch (error) {
      console.error('Error exporting order history:', error);
      dispatch(
        setSnackBarFailed({
          snackBarMessage: strings.displayText.orderHistoryExportFail,
        }),
      );
    } finally {
      setExportLoader(false);
    }
  };

  return (
    <>
      <Grid container spacing={2} alignItems="center">
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.startDate}
            type="date"
            value={filters.startDate}
            onChange={(e) => {
              handleFilterChange('startDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              max: filters.endDate ?? undefined,
            }}
          />
        </Grid>
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.endDate}
            type="date"
            value={filters.endDate}
            onChange={(e) => {
              handleFilterChange('endDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
            error={!!dateError}
            helperText={dateError}
            inputProps={{
              min: filters.startDate ?? undefined,
            }}
          />
        </Grid>
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Status</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              label="Status"
              value={filters.status}
              onChange={(e) => {
                handleFilterChange('status', e.target.value);
              }}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid sx={{ minWidth: 150, marginTop: 2, marginRight: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Order Type</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              label="Order Type"
              value={filters.orderType}
              onChange={(e) => {
                handleFilterChange('orderType', e.target.value);
              }}
            >
              {transactionTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            loadingPosition="end"
            loading={filterLoader}
            disabled={!!dateError}
            onClick={() => {
              if (dateError) {
                dispatch(
                  setSnackBarFailed({
                    snackBarMessage: dateError,
                  }),
                );
                return;
              }
              setFilterLoader(true);
              void getAllPlanRequests();
            }}
          >
            {strings.displayText.filter}
          </LoadingButton>
        </Grid>
        <Grid item>
          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            loadingPosition="end"
            loading={exportLoader}
            onClick={() => {
              void downloadExcelFile();
            }}
          >
            {strings.displayText.export}
          </LoadingButton>
        </Grid>
      </Grid>
      <CustomDataGrid
        columns={columns}
        rows={planData as GridRowData[]}
        getRowId={(row: GridRowData) => `${String(row.mmOrderId)}`}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        currentPage="orderHistory"
        pageCount={pageCount}
        sortModel={checkSortingValues(pageInfo)}
        sortingMode={strings.displayText.sortingServer}
        onSortModelChange={(mode: unknown) => {
          dispatch(
            setSortBy({
              key: 'orderHistory',
              value: mode[0].field,
            }),
          );
          dispatch(
            setSortOrder({
              key: 'orderHistory',
              value: mode[0].sort,
            }),
          );
          dispatch(
            setCurrentPage({
              key: 'orderHistory',
              value: 0,
            }),
          );
        }}
      />
      <DialogBox
        open={isModalOpen}
        title="Order Status"
        onClose={handleChatDialogClose}
      >
        <OrderStatus ibOrderId={Number(rowIBOrderId)} />
      </DialogBox>
      <DialogBox
        open={openUserModal}
        onClose={() => {
          setOpenUserModal(false);
          setUserUnits(null);
          setOrderId(null);
        }}
        title="Units Allocated"
        PaperProps={{
          sx: {
            width: '90%',
            maxWidth: '1200px',
            height: '90%',
          },
        }}
      >
        <UserUnitsModal orderId={orderId} />
      </DialogBox>
      {orderConfirmation.length > 0 && !orderConfirmation[0].order_id ? (
        <YesNoModal
          open={openConfirmationDialog}
          onClose={() => {
            setOpenConfirmationDialog(false);
          }}
          title={`${orderConfirmation[0].message[0]} ${strings.displayText.orderConfirmAgain}`}
          handleConfirm={confirmIBOrder}
        ></YesNoModal>
      ) : null}
      {ibError.open && (
        <ErrorModel
          open={ibError.open}
          title={ibError.title}
          onClose={() => {
            setIbError({ information: '', open: false, title: '' });
          }}
          information={ibError.information}
        />
      )}
    </>
  );
};

export default OrderHistory;
